const persistedLiveUpdatePrefix = 'wl_live_update_pending:';

function getSessionStorage() {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.sessionStorage || null;
    } catch (error) {
        return null;
    }
}

function sanitizePostType(postType) {
    return String(postType || '').trim().toLowerCase().replace(/[^a-z0-9_:-]+/g, '').slice(0, 64);
}

function sanitizeRefreshTraceID(refreshTraceID) {
    return String(refreshTraceID || '').replace(/[^a-zA-Z0-9:_-]+/g, '').slice(0, 96);
}

function normalizePositiveInt(value) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0;
    }

    return parsed;
}

export function normalizePersistedLiveUpdateContext(payload = null) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return null;
    }

    const postType = sanitizePostType(payload.post_type || payload.live_update_post_type);
    const updateSeq = normalizePositiveInt(payload.update_seq ?? payload.live_update_update_seq);
    const taskId = normalizePositiveInt(payload.task_id ?? payload.live_update_task_id);
    const observedAtMs = normalizePositiveInt(payload.observed_at_ms ?? payload.live_update_observed_at_ms);
    const refreshTraceID = sanitizeRefreshTraceID(payload.refresh_trace_id);

    if (!postType || updateSeq <= 0) {
        return null;
    }

    return {
        post_type: postType,
        update_seq: updateSeq,
        task_id: taskId > 0 ? taskId : undefined,
        observed_at_ms: observedAtMs > 0 ? observedAtMs : undefined,
        refresh_trace_id: refreshTraceID || undefined,
    };
}

export function comparePersistedLiveUpdateContexts(left = null, right = null) {
    const normalizedLeft = normalizePersistedLiveUpdateContext(left);
    const normalizedRight = normalizePersistedLiveUpdateContext(right);

    if (!normalizedLeft && !normalizedRight) return 0;
    if (!normalizedLeft) return -1;
    if (!normalizedRight) return 1;
    if (normalizedLeft.post_type !== normalizedRight.post_type) return 0;
    if (normalizedLeft.update_seq !== normalizedRight.update_seq) {
        return normalizedLeft.update_seq - normalizedRight.update_seq;
    }

    const observedAtCmp = normalizePositiveInt(normalizedLeft.observed_at_ms) - normalizePositiveInt(normalizedRight.observed_at_ms);
    if (observedAtCmp !== 0) {
        return observedAtCmp;
    }

    const leftTaskId = normalizePositiveInt(normalizedLeft.task_id);
    const rightTaskId = normalizePositiveInt(normalizedRight.task_id);
    if (leftTaskId > 0 && rightTaskId > 0 && leftTaskId !== rightTaskId) {
        return leftTaskId - rightTaskId;
    }

    return 0;
}

function buildStorageKey(postType = '') {
    const normalizedPostType = sanitizePostType(postType);
    return normalizedPostType ? `${persistedLiveUpdatePrefix}${normalizedPostType}` : '';
}

export function readPersistedLiveUpdateContext(postType = '') {
    const storage = getSessionStorage();
    const storageKey = buildStorageKey(postType);
    if (!storage || !storageKey) {
        return null;
    }

    const rawValue = storage.getItem(storageKey);
    if (!rawValue) {
        return null;
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        const normalizedValue = normalizePersistedLiveUpdateContext(parsedValue);
        if (!normalizedValue) {
            storage.removeItem(storageKey);
            return null;
        }

        return normalizedValue;
    } catch (error) {
        storage.removeItem(storageKey);
        return null;
    }
}

export function writePersistedLiveUpdateContext(payload = null) {
    const storage = getSessionStorage();
    const normalizedValue = normalizePersistedLiveUpdateContext(payload);
    const storageKey = buildStorageKey(normalizedValue?.post_type);
    if (!storage || !normalizedValue || !storageKey) {
        return null;
    }

    const currentValue = readPersistedLiveUpdateContext(normalizedValue.post_type);
    if (currentValue && comparePersistedLiveUpdateContexts(currentValue, normalizedValue) >= 0) {
        return currentValue;
    }

    try {
        storage.setItem(storageKey, JSON.stringify(normalizedValue));
        return normalizedValue;
    } catch (error) {
        storage.removeItem(storageKey);
        return null;
    }
}

export function clearPersistedLiveUpdateContext(postType = '', upToPayload = null) {
    const storage = getSessionStorage();
    const storageKey = buildStorageKey(postType);
    if (!storage || !storageKey) {
        return;
    }

    if (upToPayload) {
        const currentValue = readPersistedLiveUpdateContext(postType);
        const upToValue = normalizePersistedLiveUpdateContext(upToPayload);
        if (!currentValue || !upToValue || comparePersistedLiveUpdateContexts(currentValue, upToValue) > 0) {
            return;
        }
    }

    storage.removeItem(storageKey);
}
